// What kind of business this workspace is (ADR-0013).
//
// The workspace is created by a database trigger the moment someone signs in
// for the first time, so there is no signup form to ask this on. It is asked
// here instead, once, and the answer changes wording and defaults — never what
// the product will let you do. That restraint is the whole design: the failure
// mode of a "type" field is that it grows into a feature flag, and then an SMB
// that picked the wrong word in its first minute is locked out of half the app.
//
// The line for a business owner is the one worth getting right. They did not
// sign up thinking of themselves as an influencer, and telling them they are is
// not flattery — the people they need to move are their own customers, which is
// the same job with a smaller and considerably warmer audience.

import { useState } from "react";
import { useWorkspace } from "../lib/WorkspaceContext";
import {
  setWorkspaceKind, WORKSPACE_KIND, type WorkspaceKind,
} from "../lib/workspace";
import { logError } from "../lib/logger";

const KINDS: WorkspaceKind[] = ["influencer", "business", "agency"];

export function WorkspaceKindCard() {
  const { active, can, refresh } = useWorkspace();
  const [choosing, setChoosing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!active) return null;

  const current = active.kind;
  const mayChange = can("workspace.manage");
  // Unanswered is a real state, not a missing value: it is why this card is
  // asking rather than telling.
  const open = choosing || current === null;

  async function choose(kind: WorkspaceKind) {
    setBusy(true);
    setError(null);
    try {
      await setWorkspaceKind(active!.workspace_id, kind);
      await refresh();
      setChoosing(false);
    } catch (e: any) {
      setError(e.message ?? String(e));
      void logError("app", e, { step: "set_workspace_kind", workspaceId: active!.workspace_id });
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    const copy = WORKSPACE_KIND[current];
    return (
      <div className="card">
        <div className="row">
          <h2 style={{ flex: 1, margin: 0 }}>What this workspace is</h2>
          {mayChange && (
            <button className="sm" onClick={() => setChoosing(true)}>Change</button>
          )}
        </div>
        <p style={{ marginBottom: ".3rem" }}>
          <strong>{copy.label}</strong> — {copy.blurb}
        </p>
        <p className="muted" style={{ fontSize: ".875rem", marginBottom: 0 }}>{copy.pitch}</p>
      </div>
    );
  }

  return (
    <div className="card kind-card">
      <h2>What kind of workspace is this?</h2>
      <p className="muted" style={{ maxWidth: "62ch" }}>
        It changes the wording you see and what the invite form suggests. It does not change
        what you can do, what you are charged, or who can see anything — every feature is
        available to all three.
      </p>

      {error && <div className="err" style={{ marginBottom: ".8rem" }}>{error}</div>}

      <div className="kind-options">
        {KINDS.map((kind) => {
          const copy = WORKSPACE_KIND[kind];
          return (
            <button
              key={kind}
              type="button"
              className={current === kind ? "kind-option on" : "kind-option"}
              disabled={busy || !mayChange}
              onClick={() => choose(kind)}
            >
              <strong>{copy.label}</strong>
              <span className="muted">{copy.blurb}</span>
            </button>
          );
        })}
      </div>

      {!mayChange && (
        <p className="muted" style={{ fontSize: ".82rem" }}>
          Only someone who can manage this workspace can answer this.
        </p>
      )}

      {choosing && current !== null && (
        <button className="sm" disabled={busy} onClick={() => setChoosing(false)}>Cancel</button>
      )}
    </div>
  );
}
